<template>
    <aside class="student-dashboard__sidebar" aria-label="Student navigation">
        <div class="student-dashboard__profile">
            <div class="student-dashboard__avatar" aria-hidden="true">
                <img :src="profileImage" alt="" />
            </div>
            <div class="student-dashboard__identity">
                <strong>{{ fullName }}</strong>
                <span class="student-dashboard__status-text">{{ studentStatus }}</span>
            </div>
            <button type="button" class="student-dashboard__collapse" aria-label="Collapse menu">
                <i class="ri-contract-left-line"></i>
            </button>
        </div>

        <nav class="student-dashboard__menu">
            <a
                v-for="item in navItems"
                :key="item.label"
                href="#"
                class="student-dashboard__menu-link"
                :class="{ 'is-active': activeMenu === item.label }"
                @click.prevent="handleMenuClick(item)"
            >
                <i :class="item.icon"></i>
                <span>{{ item.label }}</span>
                <i v-if="item.hasChildren" class="ri-arrow-right-s-line student-dashboard__menu-arrow"></i>
            </a>
        </nav>

        <button type="button" class="student-dashboard__logout" @click="logout">
            <i class="ri-logout-circle-line"></i>
            <span>Logout</span>
        </button>
    </aside>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    student: {
        type: Object,
        default: () => ({}),
    },
});

const getActiveMenu = () => {
    const path = window.location.pathname;

    if (path.includes('/student/profile')) {
        return 'My Profile';
    }

    if (path.includes('/student/enrolled-courses')) {
        return 'Enrolled Courses';
    }

    if (path.includes('/student/payment-history')) {
        return 'Payment History';
    }

    if (path.includes('/online-training/cart')) {
        return 'My Cart';
    }

    if (path.includes('/student/quizzes')) {
        return 'Quizzes';
    }

    if (path.includes('/student/exams')) {
        return 'Exams';
    }

    if (path.includes('/student/assignments')) {
        return 'Assignments';
    }

    if (path.includes('/student/certificates')) {
        return 'My Certificate';
    }

    if (path.includes('/dashboard/my-course')) {
        return 'Enrolled Courses';
    }

    return 'Dashboard';
};

const activeMenu = ref(getActiveMenu());
const defaultProfileImage = '/media/default/default-image.jpg';

const navItems = [
    { label: 'Dashboard', icon: 'ri-dashboard-line', href: '/student/dashboard' },
    { label: 'My Profile', icon: 'ri-user-3-line', href: '/student/profile' },
    { label: 'My Cart', icon: 'ri-shopping-cart-2-line', href: '/online-training/cart' },
    { label: 'Enrolled Courses', icon: 'ri-bookmark-3-line', href: '/student/enrolled-courses' },
    { label: 'Payment History', icon: 'ri-bank-card-line', href: '/student/payment-history' },
    { label: 'Exams', icon: 'ri-file-paper-2-line', href: '/student/exams' },
    { label: 'Quizzes', icon: 'ri-question-line', href: '/student/quizzes' },
    { label: 'Assignments', icon: 'ri-file-list-3-line', href: '/student/assignments' },
    { label: 'My Certificate', icon: 'ri-award-line', href: '/student/certificates' },
];

const fullName = computed(() => {
    const name = [props.student.name, props.student.last_name].filter(Boolean).join(' ').trim();
    return name || 'Student';
});

const profileImage = computed(() => props.student.profile_path || defaultProfileImage);

const studentStatus = computed(() => {
    const status = props.student.status || 'active';
    return status.charAt(0).toUpperCase() + status.slice(1);
});

const handleMenuClick = (item) => {
    if (item.href) {
        window.location.href = item.href;
        return;
    }

    activeMenu.value = item.label;
};


const logout = () => {
    if (typeof window.studentLogout === 'function') {
        window.studentLogout();
    }
};
</script>

<style scoped>
.student-dashboard__sidebar {
    position: sticky;
    top: 18px;
    align-self: start;
    height: calc(100vh - 36px);
    max-height: calc(100vh - 36px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 20;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);
}

.student-dashboard__profile {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr) 34px;
    gap: 10px;
    align-items: center;
    padding: 18px 16px;
    border-bottom: 1px solid #eef2f7;
}

.student-dashboard__avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #fff7ed;
    overflow: hidden;
}

.student-dashboard__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.student-dashboard__identity {
    min-width: 0;
}

.student-dashboard__identity strong,
.student-dashboard__identity span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.student-dashboard__identity strong {
    color: #111827;
    font-size: 14px;
    font-weight: 700;
}

.student-dashboard__identity .student-dashboard__status-text {
    display: inline-flex;
    align-items: center;
    width: max-content;
    max-width: 100%;
    gap: 8px;
    overflow: visible;
    text-overflow: clip;
    color: #16a34a;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

.student-dashboard__status-text::before {
    display: block;
    flex: 0 0 8px;
    width: 8px;
    height: 8px;
    aspect-ratio: 1;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.62);
    content: "";
    animation: studentStatusPulse 1.55s ease-out infinite;
}

@keyframes studentStatusPulse {
    0% {
        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.62);
    }
    72% {
        box-shadow: 0 0 0 8px rgba(34, 197, 94, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
}

.student-dashboard__collapse {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border: 1px solid #dbe4ef;
    border-radius: 8px;
    background: #ffffff;
    color: #64748b;
    transition: all 0.2s ease;
}

.student-dashboard__collapse:hover {
    border-color: #0d6efd;
    color: #0d6efd;
}

.student-dashboard__menu {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 12px;
    overflow-y: auto;
}

.student-dashboard__menu-link {
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr) 18px;
    align-items: center;
    gap: 8px;
    min-height: 40px;
    padding: 8px 10px;
    border-radius: 8px;
    color: #4b5563;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s ease;
}

.student-dashboard__menu-link i {
    font-size: 17px;
}

.student-dashboard__menu-link span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.student-dashboard__menu-link:hover,
.student-dashboard__menu-link.is-active {
    background: #e7f1ff;
    color: #0d6efd;
}

.student-dashboard__menu-arrow {
    color: #9ca3af;
}

.student-dashboard__menu-link:hover .student-dashboard__menu-arrow,
.student-dashboard__menu-link.is-active .student-dashboard__menu-arrow {
    color: #0d6efd;
}

.student-dashboard__logout {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 18px 12px 14px;
    min-height: 42px;
    padding: 10px 12px;
    border: 0;
    border-radius: 8px;
    background: #fff1f2;
    color: #be123c;
    font-size: 13px;
    font-weight: 700;
}

@media (max-width: 991px) {
    .student-dashboard__sidebar {
        position: static;
        height: auto;
        max-height: none;
    }

    .student-dashboard__menu {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        max-height: none;
    }

    .student-dashboard__logout {
        margin-top: 8px;
    }
}

@media (max-width: 575px) {
    .student-dashboard__menu {
        grid-template-columns: 1fr;
    }
}
</style>
